<template>
  <settings-modal class="bank-details-edit-modal t-bank-details-edit-modal">
    <template slot="heading">{{ label }}</template>

    <v-text-field
      v-model="form.name"
      :disabled="!isEditable"
      class="t-name-field"
      label="Name"
      :error-messages="getValidationErrorMessage('name')"
    />

    <v-text-field
      v-model="form.accountNumber"
      :disabled="!isEditable"
      class="t-bank-iban-field"
      label="IBAN"
      :error-messages="getValidationErrorMessage('accountNumber')"
      @input="onIbanChange"
    />

    <v-text-field
      v-model="form.bankSwift"
      :disabled="!isEditable && isBankSwiftSpecified"
      class="t-bank-swift-field"
      label="Bank SWIFT"
      :error-messages="getValidationErrorMessage('bankSwift')"
    />

    <v-text-field
      v-model="form.bankName"
      :disabled="!isEditable"
      class="t-bank-name-field"
      label="Bank name"
      :error-messages="getValidationErrorMessage('bankName')"
    />

    <v-text-field
      v-model="form.bankCountry"
      :disabled="!isEditable"
      class="t-bank-country-field"
      label="Bank country"
      :error-messages="getValidationErrorMessage('bankCountry')"
    />

    <v-text-field
      v-model="form.bankAddress"
      :disabled="!isEditable"
      class="t-bank-address-field"
      label="Bank address"
      :error-messages="getValidationErrorMessage('bankAddress')"
    />

    <error-alert :error="error" />

    <template slot="actions">
      <v-btn
        class="t-save-button"
        color="primary"
        medium
        flat
        :disabled="isSubmitDisabled"
        @click="onSaveButtonClick"
      >save</v-btn>

      <v-btn
        class="t-cancel-button"
        color="primary"
        medium
        flat
        @click="onCancelButtonClick"
      >cancel</v-btn>
    </template>
  </settings-modal>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  import { debounce, isEqual } from 'lodash'

  // Events
  import { EVENT_PAYMENT_UPDATE } from '@/events/payments'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'

  // Mixins
  import form from '@/mixins/form'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import ErrorAlert from '@/components/ui/ErrorAlert'
  import AccountApi from '@/services/Api/AccountApi'
  import flashMessages from '@/mixins/flashMessages'

  export default {
    name: 'BankDetailsEditModal',
    components: {
      SettingsModal,
      ErrorAlert
    },
    mixins: [form, flashMessages],
    data () {
      return {
        form: {
          accountNumber: null,
          bankSwift: null,
          bankName: null,
          bankCountry: null,
          bankAddress: null,
          name: null
        },
        requiredFields: [
          'accountNumber',
          'bankSwift',
          'bankName',
          'bankCountry',
          'bankAddress',
          'name'
        ],
        resolveRecipientBankDataViaIbanApiRequest: new ApiRequest(AccountApi.resolveIban),
        paymentBankDetailsUpdateApiRequest: new ApiRequest(AccountApi.updateBankDetails)
      }
    },
    computed: {
      ...mapGetters('modal', ['modal']),
      isEditable () {
        return !this.isNameSpecified || !this.isAccountNumberSpecified
      },
      isBankSwiftSpecified () {
        if (this.payment.isWithdrawal) {
          return this.payment.recipientBankSwift != null && this.payment.recipientBankSwift !== ''
        }

        return this.payment.senderBankSwift != null && this.payment.senderBankSwift !== ''
      },
      isNameSpecified () {
        if (this.payment.isWithdrawal) {
          return this.payment.recipientName != null && this.payment.recipientName !== ''
        }

        return this.payment.senderName != null && this.payment.senderName !== ''
      },
      isAccountNumberSpecified () {
        if (this.payment.isWithdrawal) {
          return this.payment.recipientBankAccountNumber != null && this.payment.recipientBankAccountNumber !== ''
        }

        return this.payment.senderBankAccountNumber != null && this.payment.senderBankAccountNumber !== ''
      },
      initialFormData () {
        if (this.payment.isWithdrawal) {
          return {
            accountNumber: this.payment.recipientBankAccountNumber,
            bankSwift: this.payment.recipientBankSwift,
            bankName: this.payment.recipientBankName,
            bankCountry: this.payment.recipientBankCountry,
            bankAddress: this.payment.recipientBankAddress,
            name: this.payment.recipientName
          }
        } else {
          return {
            accountNumber: this.payment.senderBankAccountNumber,
            bankSwift: this.payment.senderBankSwift,
            bankName: this.payment.senderBankName,
            bankCountry: this.payment.senderBankCountry,
            bankAddress: this.payment.senderBankAddress,
            name: this.payment.senderName
          }
        }
      },
      label () {
        return this.payment.isWithdrawal ? 'Edit recipient bank details' : 'Edit sender bank details'
      },
      payment () {
        return this.modal.data.payment
      },
      isFormDataEqual () {
        return isEqual(this.initialFormData, this.form)
      },
      isSubmitDisabled () {
        return this.loading || !this.isFormFilled || this.isFormDataEqual
      },
      loading () {
        return this.paymentBankDetailsUpdateApiRequest.loading
      },
      error () {
        return this.paymentBankDetailsUpdateApiRequest.error
      }
    },
    created () {
      this.setFormData(this.initialFormData)
    },
    methods: {
      ...mapActions('modal', ['closeModal']),
      onIbanChange: debounce(function () {
        this.resolveRecipientBankDataViaIbanApiRequest.execute(this.form.accountNumber)
          .then((bankData) => {
            this.setFormData(bankData)
          })
          .catch(() => {})
      }, 500),
      onSaveButtonClick () {
        this.paymentBankDetailsUpdateApiRequest.execute(this.payment.id, this.parsedFormInput)
          .then(() => {
            this.$eventHub.$emit(EVENT_PAYMENT_UPDATE)
            this.closeModal()
            this.showSuccessFlashMessage('Bank details has been successfully updated.')
          })
          .catch(() => {})
      },
      onCancelButtonClick () {
        this.closeModal()
      }
    }
  }
</script>
